/**
 * Illustrated unit counters — the shared artwork. Every silhouette is SVG path data, built once
 * at module load from a few drawing primitives, so the map atlas (Canvas2D `Path2D`, see
 * counters.ts) and the HUD (`<path d>`, ui/common/UnitCounter.tsx) print exactly the same art.
 *
 * Art direction (docs/Visual-Style.md): a die-cut cardboard counter in the faction colour, the
 * arm of service shown as a side-view silhouette in the faction's dark ink, lit from the upper
 * left — lighter "mid" planes give the surfaces facing the lamp some volume and hairline
 * highlights catch the top edges. At whole-map zoom only the silhouette survives (tank, truck,
 * gun, rifleman, horseman, staff car must differ by outline alone); close up the details read.
 *
 * Coordinates: silhouettes are drawn in an ART_W × ART_H art box (x right, y down, ground at
 * y ≈ 69, everything faces right = towards the enemy) that maps onto the counter frame
 * (48 × 32 layout units at (6, 12)) by `ART_SCALE` and `ART_X`, `ART_Y`.
 */
import type { UnitType } from '@friction/engine';
import { darken, lighten, mixColor } from '../palette';

/** Art box (3:2, like the frame). */
export const ART_W = 120;
export const ART_H = 80;
/** Art box → counter box: scale 0.4 and the frame's top-left corner. */
export const ART_SCALE = 48 / ART_W;
export const ART_X = 6;
export const ART_Y = 12;

/**
 * Tones of the print. `ink` the silhouette, `mid` surfaces lit by the lamp, `hi` hairline
 * highlights, `face` the counter face (knock-outs), `flag` a pale pennant cloth.
 */
export type Tone = 'ink' | 'mid' | 'hi' | 'face' | 'flag';

export interface ArtLayer {
  d: string;
  tone: Tone;
  /** Stroke width in art units; absent = fill. Strokes are round-capped. */
  stroke?: number;
  /** Fill with the even-odd rule (cut-outs such as windows, spoked wheels). */
  evenOdd?: boolean;
}

export interface UnitArt {
  layers: readonly ArtLayer[];
  /**
   * The silhouette's outer shape (union of its ink parts), for the "hollow" print of dummy
   * formations: stroked wide in ink, then filled with the counter face.
   */
  outline: string;
}

// ---------------------------------------------------------------------------------------------
// Path builder
// ---------------------------------------------------------------------------------------------

const f = (v: number) => String(Math.round(v * 100) / 100);

type Seg =
  | { c: 'L'; x: number; y: number }
  | { c: 'C'; x1: number; y1: number; x2: number; y2: number; x: number; y: number }
  | { c: 'Q'; x1: number; y1: number; x: number; y: number }
  | { c: 'A'; r: number; large: boolean; sweep: boolean; x: number; y: number };

interface Subpath {
  x: number;
  y: number;
  segs: Seg[];
  closed: boolean;
}

/** Shoelace area of a closed subpath through its end and control points (> 0 = clockwise on screen). */
function signedArea(sp: Subpath): number {
  const pts: number[] = [sp.x, sp.y];
  for (const g of sp.segs) {
    if (g.c === 'C') pts.push(g.x1, g.y1, g.x2, g.y2);
    else if (g.c === 'Q') pts.push(g.x1, g.y1);
    pts.push(g.x, g.y);
  }
  let a = 0;
  for (let i = 0; i < pts.length; i += 2) {
    const j = (i + 2) % pts.length;
    a += pts[i] * pts[j + 1] - pts[j] * pts[i + 1];
  }
  return a / 2;
}

/** The same closed outline traversed the other way round. */
function reversed(sp: Subpath): Subpath {
  const ends = [[sp.x, sp.y], ...sp.segs.map((g) => [g.x, g.y])];
  const last = ends[ends.length - 1];
  const segs: Seg[] = [];
  if (last[0] !== sp.x || last[1] !== sp.y) segs.push({ c: 'L', x: last[0], y: last[1] });
  for (let i = sp.segs.length - 1; i >= 0; i--) {
    const g = sp.segs[i];
    const [x, y] = ends[i];
    if (g.c === 'L') segs.push({ c: 'L', x, y });
    else if (g.c === 'C') segs.push({ c: 'C', x1: g.x2, y1: g.y2, x2: g.x1, y2: g.y1, x, y });
    else if (g.c === 'Q') segs.push({ c: 'Q', x1: g.x1, y1: g.y1, x, y });
    else segs.push({ c: 'A', r: g.r, large: g.large, sweep: !g.sweep, x, y });
  }
  return { x: sp.x, y: sp.y, segs, closed: true };
}

function serialize(sp: Subpath): string {
  let d = `M${f(sp.x)} ${f(sp.y)}`;
  for (const g of sp.segs) {
    if (g.c === 'L') d += `L${f(g.x)} ${f(g.y)}`;
    else if (g.c === 'C') d += `C${f(g.x1)} ${f(g.y1)} ${f(g.x2)} ${f(g.y2)} ${f(g.x)} ${f(g.y)}`;
    else if (g.c === 'Q') d += `Q${f(g.x1)} ${f(g.y1)} ${f(g.x)} ${f(g.y)}`;
    else d += `A${f(g.r)} ${f(g.r)} 0 ${g.large ? 1 : 0} ${g.sweep ? 1 : 0} ${f(g.x)} ${f(g.y)}`;
  }
  return sp.closed ? `${d}Z` : d;
}

/**
 * Tiny SVG path writer with a translate + uniform-scale transform (used to place the same
 * soldier twice). Subpaths of one layer overlap freely: every closed subpath is written
 * clockwise, so under the non-zero fill rule they always union into one solid silhouette (a
 * counter-clockwise limb over a clockwise head would otherwise punch a hole).
 */
class Pen {
  private readonly out: Subpath[];
  private readonly tx: number;
  private readonly ty: number;
  private readonly s: number;

  constructor(out: Subpath[] = [], tx = 0, ty = 0, s = 1) {
    this.out = out;
    this.tx = tx;
    this.ty = ty;
    this.s = s;
  }

  /** A pen writing into the same path with another origin and scale. */
  at(tx: number, ty: number, s = 1): Pen {
    return new Pen(this.out, this.tx + tx * this.s, this.ty + ty * this.s, this.s * s);
  }

  private X(x: number): number {
    return this.tx + x * this.s;
  }
  private Y(y: number): number {
    return this.ty + y * this.s;
  }
  private get cur(): Subpath {
    return this.out[this.out.length - 1];
  }

  M(x: number, y: number): this {
    this.out.push({ x: this.X(x), y: this.Y(y), segs: [], closed: false });
    return this;
  }
  L(x: number, y: number): this {
    this.cur.segs.push({ c: 'L', x: this.X(x), y: this.Y(y) });
    return this;
  }
  C(x1: number, y1: number, x2: number, y2: number, x: number, y: number): this {
    const [X, Y] = [(v: number) => this.X(v), (v: number) => this.Y(v)];
    this.cur.segs.push({ c: 'C', x1: X(x1), y1: Y(y1), x2: X(x2), y2: Y(y2), x: X(x), y: Y(y) });
    return this;
  }
  Q(x1: number, y1: number, x: number, y: number): this {
    this.cur.segs.push({ c: 'Q', x1: this.X(x1), y1: this.Y(y1), x: this.X(x), y: this.Y(y) });
    return this;
  }
  A(r: number, large: boolean, sweep: boolean, x: number, y: number): this {
    this.cur.segs.push({ c: 'A', r: r * this.s, large, sweep, x: this.X(x), y: this.Y(y) });
    return this;
  }
  Z(): this {
    const sp = this.cur;
    sp.closed = true;
    if (signedArea(sp) < 0) this.out[this.out.length - 1] = reversed(sp);
    return this;
  }

  /** Closed polygon through the points [x0, y0, x1, y1, …]. */
  poly(...xy: number[]): this {
    this.M(xy[0], xy[1]);
    for (let i = 2; i < xy.length; i += 2) this.L(xy[i], xy[i + 1]);
    return this.Z();
  }

  /** Circle as four quarter arcs (so its winding can be measured like any polygon). */
  circle(cx: number, cy: number, r: number): this {
    return this.M(cx - r, cy)
      .A(r, false, true, cx, cy - r)
      .A(r, false, true, cx + r, cy)
      .A(r, false, true, cx, cy + r)
      .A(r, false, true, cx - r, cy)
      .Z();
  }

  rrect(x: number, y: number, w: number, h: number, r: number): this {
    return this.M(x + r, y)
      .L(x + w - r, y)
      .Q(x + w, y, x + w, y + r)
      .L(x + w, y + h - r)
      .Q(x + w, y + h, x + w - r, y + h)
      .L(x + r, y + h)
      .Q(x, y + h, x, y + h - r)
      .L(x, y + r)
      .Q(x, y, x + r, y)
      .Z();
  }

  /**
   * Tapered capsule: the convex hull of two discs (x1, y1, r1) and (x2, y2, r2) — limbs, barrels,
   * trails. A chain of capsules sharing their joint discs draws a smooth jointed limb.
   */
  limb(x1: number, y1: number, r1: number, x2: number, y2: number, r2: number): this {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const d = Math.hypot(dx, dy);
    if (d <= Math.abs(r1 - r2)) return this.circle(r1 > r2 ? x1 : x2, r1 > r2 ? y1 : y2, Math.max(r1, r2));
    const th = Math.atan2(dy, dx);
    const b = Math.acos((r1 - r2) / d);
    const pt = (x: number, y: number, r: number, a: number): [number, number] => [
      x + r * Math.cos(a),
      y + r * Math.sin(a),
    ];
    const a1 = pt(x1, y1, r1, th + b);
    const a2 = pt(x2, y2, r2, th + b);
    const b2 = pt(x2, y2, r2, th - b);
    const b1 = pt(x1, y1, r1, th - b);
    return this.M(a1[0], a1[1])
      .L(a2[0], a2[1])
      .A(r2, 2 * b > Math.PI, false, b2[0], b2[1])
      .L(b1[0], b1[1])
      .A(r1, 2 * b < Math.PI, false, a1[0], a1[1])
      .Z();
  }

  /** Chain of tapered capsules through [x, y, r] joints. */
  chain(...pts: [number, number, number][]): this {
    for (let i = 1; i < pts.length; i++) this.limb(...pts[i - 1], ...pts[i]);
    return this;
  }

  /** Open arc on a circle from angle a0 to a1 (degrees, clockwise on screen) — highlight rims. */
  arc(cx: number, cy: number, r: number, a0: number, a1: number): this {
    const rad = Math.PI / 180;
    this.M(cx + r * Math.cos(a0 * rad), cy + r * Math.sin(a0 * rad));
    return this.A(r, a1 - a0 > 180, true, cx + r * Math.cos(a1 * rad), cy + r * Math.sin(a1 * rad));
  }

  /** Open polyline [x0, y0, x1, y1, …] — highlight and detail strokes. */
  line(...xy: number[]): this {
    this.M(xy[0], xy[1]);
    for (let i = 2; i < xy.length; i += 2) this.L(xy[i], xy[i + 1]);
    return this;
  }

  toString(): string {
    return this.out.map(serialize).join('');
  }
}

/** Collects the layers of one silhouette. */
class Art {
  readonly layers: ArtLayer[] = [];
  private readonly outlineParts: string[] = [];

  /** Adds a layer drawn by `draw`; ink fills also make up the outline unless `outline: false`. */
  add(
    tone: Tone,
    draw: (p: Pen) => void,
    opts: { stroke?: number; evenOdd?: boolean; outline?: boolean } = {},
  ) {
    const p = new Pen();
    draw(p);
    const d = p.toString();
    this.layers.push({ d, tone, stroke: opts.stroke, evenOdd: opts.evenOdd });
    if (tone === 'ink' && opts.stroke === undefined && opts.outline !== false) this.outlineParts.push(d);
    return this;
  }

  /** Adds to the outline only (parts drawn open, like a spoked wheel, that are solid in outline). */
  shape(draw: (p: Pen) => void) {
    const p = new Pen();
    draw(p);
    this.outlineParts.push(p.toString());
    return this;
  }

  build(): UnitArt {
    return { layers: this.layers, outline: this.outlineParts.join('') };
  }
}

// ---------------------------------------------------------------------------------------------
// Silhouettes
// ---------------------------------------------------------------------------------------------

/** Upper-left rim highlight on a round part (the lamp stands top-left). */
const rim = (p: Pen, cx: number, cy: number, r: number) => p.arc(cx, cy, r, 185, 275);

/** Medium tank, 1940-ish and of no particular nation: sloped glacis, turret, long gun. */
function tank(): UnitArt {
  const wheels = [27, 42.5, 58, 73.5, 89];
  const a = new Art();
  a.add('ink', (p) => {
    // track run: flat on the ground, raised at the drive sprocket (front) and idler (rear)
    p.M(25, 69.5).L(92, 69.5).C(99, 69.5, 104.5, 64.5, 104.5, 58.5).C(104.5, 53, 100.5, 49, 95, 49);
    p.L(21, 49).C(15.5, 49, 12, 53, 12, 57.5).C(12, 63.5, 17.5, 69.5, 25, 69.5).Z();
    // hull with track guards; sloped glacis in front, engine deck behind
    p.poly(8, 51, 108, 51, 108, 47, 95.5, 35, 20, 35, 11.5, 40.5, 8, 46.5);
    // driver's hood on the glacis
    p.poly(84, 36, 86, 32.2, 92.5, 32.2, 95.5, 36);
    // turret: sloped front, rounded rear
    p.M(34.5, 35.5)
      .L(37.8, 24.6)
      .C(38.7, 21.4, 40.6, 20, 43.6, 20)
      .L(66.5, 20)
      .C(68.5, 20, 69.8, 20.8, 70.8, 22.6);
    p.L(75, 30.5).L(75, 35.5).Z();
    // commander's cupola
    p.M(43, 20.5)
      .L(44.2, 15.8)
      .C(44.5, 14.8, 45.2, 14.2, 46.4, 14.2)
      .L(53.6, 14.2)
      .C(54.8, 14.2, 55.5, 14.8, 55.8, 15.8);
    p.L(57, 20.5).Z();
    // mantlet, gun, muzzle brake
    p.rrect(72.5, 23.2, 6.5, 10, 1.6);
    p.poly(78, 26.2, 108.5, 25.7, 108.5, 29.5, 78, 30.3);
    p.rrect(107, 24.2, 6.6, 6.8, 1.2);
  });
  // whip antenna (reads only close up)
  a.add('ink', (p) => p.limb(38.5, 23, 0.75, 27, 6.5, 0.4), { outline: false });
  a.add('mid', (p) => {
    // hull side and turret side face the lamp
    p.poly(20.6, 37.4, 94.6, 37.4, 104, 46.2, 11.3, 46.2, 12.8, 41.6);
    p.M(38, 34)
      .L(40.4, 25.8)
      .C(41, 23.6, 42.1, 22.6, 44.4, 22.6)
      .L(65.6, 22.6)
      .C(66.8, 22.6, 67.6, 23.1, 68.3, 24.4);
    p.L(72, 31).L(72, 34).Z();
    for (const x of wheels) p.circle(x, 59.5, 6.3);
    p.circle(98.2, 57.2, 3.3);
    p.circle(18.8, 57.4, 3.1);
  });
  a.add('ink', (p) => {
    for (const x of wheels) p.circle(x, 59.5, 2.1);
    // hull detail: track-guard shadow line, engine louvres
    p.poly(12, 46.2, 104, 46.2, 104, 47.2, 12, 47.2);
    for (const x of [16, 20, 24]) p.poly(x, 39.5, x + 1.3, 39.5, x + 0.2, 44.5, x - 1.1, 44.5);
  });
  a.add(
    'hi',
    (p) => {
      p.line(21, 36.2, 94.6, 36.2, 103.5, 44.8);
      p.line(44, 21.3, 66, 21.3, 69.2, 24.4);
      p.line(46.5, 15.3, 53.5, 15.3);
      p.line(79, 27, 107.5, 26.6);
      for (const x of wheels) rim(p, x, 59.5, 5.1);
      p.line(22, 50, 94, 50);
    },
    { stroke: 0.9 },
  );
  return a.build();
}

/** Steel helmet (dome and a narrow brim) centred on x, brim at y; `w` = dome width. */
function helmet(p: Pen, x: number, y: number, w: number): void {
  const r = w / 2;
  p.M(x - r, y)
    .C(x - r, y - r * 1.12, x + r, y - r * 1.12, x + r, y)
    .Z();
  p.rrect(x - r - 1.3, y - 0.9, w + 2.6, 1.7, 0.85);
}

/** Cargo truck with a squad in the back: helmets and shoulders over the side boards. */
function truck(): UnitArt {
  const wheels: [number, number][] = [
    [29, 60.5],
    [48, 60.5],
    [95, 60.5],
  ];
  // the squad on the benches: helmet x, brim y, width
  const men: [number, number, number][] = [
    [14.4, 29.6, 8.8],
    [25.8, 28.8, 9.2],
    [37.2, 29.8, 8.6],
    [48.6, 29, 9.2],
    [60, 29.6, 8.8],
  ];
  const a = new Art();
  a.add('ink', (p) => {
    // chassis rail
    p.rrect(7, 48.5, 102, 5.5, 1.2);
    // the squad on the benches: shoulders, heads, helmets, rifles held upright
    for (const [x, y, w] of men) {
      p.rrect(x - 4.3, y + 3.2, 8.6, 8, 3);
      p.circle(x + 0.9, y + 1.5, 2.7);
      helmet(p, x, y, w);
    }
    p.limb(20, 32, 0.95, 18.6, 19.6, 0.6);
    p.limb(41, 32, 0.95, 42.8, 19, 0.6);
    p.limb(62.4, 32, 0.95, 64.6, 20.6, 0.6);
    // cargo bed (side boards)
    p.poly(7, 51, 7, 38, 70, 38, 70, 51);
    // cab
    p.M(70, 51)
      .L(70, 24.6)
      .C(70, 22.6, 71.4, 21.6, 73.4, 21.6)
      .L(86.4, 21.6)
      .C(88.2, 21.6, 89.3, 22.6, 89.7, 24.4);
    p.L(91.4, 35.5).L(91.4, 51).Z();
    // bonnet and radiator
    p.M(89, 36.2).L(104.2, 37).C(107, 37.2, 108.8, 38.8, 108.8, 41.6).L(108.8, 50).L(89, 50).Z();
    // front mudguard
    p.M(84, 52.5).C(86, 45.6, 90.6, 43.2, 95.5, 43.2).C(101.6, 43.2, 106.6, 46.4, 110.8, 52.5).Z();
    // wheels (twin rear axle)
    for (const [x, y] of wheels) p.circle(x, y, 8.6);
    // headlamp
    p.circle(104.5, 35.4, 2.4);
  });
  // the cab window: the counter shows through
  a.add('face', (p) => p.poly(73.8, 25.4, 86, 25.4, 87.6, 34.4, 73.8, 34.4));
  a.add('mid', (p) => {
    p.poly(9.2, 40.4, 67.8, 40.4, 67.8, 48.4, 9.2, 48.4);
    p.poly(72.2, 37, 89.2, 37, 89.2, 48.6, 72.2, 48.6);
    p.poly(91.2, 39.2, 104.4, 39.8, 106.6, 41.8, 106.6, 48, 91.2, 48);
    for (const [x, y] of wheels) p.circle(x, y, 5);
    for (const [x, y, w] of men)
      p.M(x - w * 0.34, y - 1.4)
        .C(x - w * 0.34, y - 4.6, x + w * 0.2, y - 5, x + w * 0.32, y - 2)
        .Z();
  });
  a.add('ink', (p) => {
    for (const [x, y] of wheels) p.circle(x, y, 1.9);
    // plank seam, door line, bonnet louvres
    p.poly(9.2, 44, 67.8, 44, 67.8, 45, 9.2, 45);
    p.poly(80.4, 37, 81.3, 37, 81.3, 48.6, 80.4, 48.6);
    for (const x of [95, 98, 101]) p.poly(x, 41, x + 0.9, 41, x + 0.9, 46.5, x, 46.5);
  });
  a.add(
    'hi',
    (p) => {
      p.line(8, 38.8, 69, 38.8);
      p.line(73.4, 22.4, 86.4, 22.4, 88.8, 24.5);
      p.line(90, 37, 104, 37.8);
      p.arc(95.5, 52.5, 11.6, 200, 305);
      for (const [x, y] of wheels) rim(p, x, y, 7.3);
      for (const [x, y, w] of men) p.arc(x, y, w * 0.42, 200, 262);
    },
    { stroke: 0.9 },
  );
  return a.build();
}

/** Field gun in battery: barrel raised 25°, shield plate, spoked wheel, trail dug in at the spade. */
function fieldGun(): UnitArt {
  const cx = 54;
  const cy = 53;
  // the bore line: breech (bx, by) → muzzle (mx, my)
  const [bx, by, mx, my] = [42, 47.4, 106, 17.6];
  const along = (t: number): [number, number] => [bx + (mx - bx) * t, by + (my - by) * t];
  const a = new Art();
  a.add('ink', (p) => {
    // trail to the spade
    p.limb(cx, cy, 4.2, 14, 65.6, 2.9);
    p.poly(8.6, 60.2, 14.8, 62, 13.2, 69.6, 6.4, 68.2);
    // cradle and breech ring
    p.limb(bx - 1, by + 0.6, 4.6, ...along(0.18), 4.2);
    // barrel, recuperator underneath, muzzle
    p.limb(bx, by, 3.9, mx, my, 2.5);
    p.limb(...along(0.1), 3.8, ...along(0.55), 3.2);
    p.limb(...along(0.93), 3.2, mx + 1, my - 0.5, 3.2);
    // shield plate on the axle, seen edge-on, top bent forward
    p.poly(56.6, 22.6, 61.6, 21, 66.4, 23.6, 67.2, 48.4, 61.2, 48.4);
  });
  a.add('mid', (p) => {
    p.poly(58.8, 24.2, 61.8, 23.2, 64.6, 24.8, 65.2, 46.4, 62.8, 46.4);
    const [x0, y0] = along(0.14);
    const [x1, y1] = along(0.52);
    p.limb(x0 + 1.2, y0 + 2.4, 1.6, x1 + 0.9, y1 + 2, 1.3);
  });
  // wheel in front of it all: tyre ring, spokes, hub
  a.add(
    'ink',
    (p) => {
      p.circle(cx, cy, 16.2);
      p.circle(cx, cy, 12.6);
    },
    { evenOdd: true, outline: false },
  );
  a.add(
    'ink',
    (p) => {
      p.circle(cx, cy, 3.9);
      for (let i = 0; i < 12; i++) {
        const t = (i / 12) * Math.PI * 2 + 0.13;
        const c = Math.cos(t);
        const s = Math.sin(t);
        const w0 = 1.1;
        const w1 = 0.75;
        p.poly(
          cx + c * 3.5 - s * w0,
          cy + s * 3.5 + c * w0,
          cx + c * 13 - s * w1,
          cy + s * 13 + c * w1,
          cx + c * 13 + s * w1,
          cy + s * 13 - c * w1,
          cx + c * 3.5 + s * w0,
          cy + s * 3.5 - c * w0,
        );
      }
    },
    { outline: false },
  );
  a.shape((p) => p.circle(cx, cy, 16.2));
  a.add('mid', (p) => p.circle(cx, cy, 2.4));
  a.add(
    'hi',
    (p) => {
      const [x0, y0] = along(0.05);
      const [x1, y1] = along(0.97);
      p.line(x0 - 0.8, y0 - 2.6, x1 - 0.6, y1 - 1.8);
      p.line(57.6, 22.4, 61.6, 21.8, 65.6, 23.8);
      p.line(50, 51.6, 17, 63.4);
      rim(p, cx, cy, 14.8);
    },
    { stroke: 0.9 },
  );
  return a.build();
}

/** One rifleman advancing at the high port: helmet, pack, rifle with bayonet (60 units tall). */
function rifleman(p: Pen): void {
  helmet(p, 22, 8.6, 12.4);
  // head and neck
  p.circle(23.4, 11.6, 3.7);
  p.limb(21.4, 13, 2.3, 20.6, 17, 2.4);
  // torso leaning into the advance, pack on the back
  p.limb(21.4, 20, 5.6, 16.8, 32.4, 4.8);
  p.rrect(9.4, 16.6, 7.8, 12.6, 2.4);
  // legs: front leg striding, rear leg pushing off
  p.chain([18.4, 33.4, 4.3], [25.6, 44.2, 3.2], [27.6, 55.4, 2.4]);
  p.limb(26.4, 57.4, 2.5, 32.2, 58.4, 1.6);
  p.chain([15.4, 33.6, 4.3], [10.2, 44.4, 3.2], [5.2, 54.2, 2.3]);
  p.limb(5, 55.6, 2.3, 9.6, 58.4, 1.6);
  // rifle at the high port with fixed bayonet
  p.limb(11.8, 39.4, 2.3, 17.6, 31.4, 1.6);
  p.limb(17.4, 31.6, 1.35, 34, 9.2, 0.9);
  p.limb(34, 9.2, 0.7, 37.6, 3.4, 0.25);
  // arms: rear hand at the small of the stock, front hand on the fore-end
  p.chain([19.6, 19.6, 2.8], [15.6, 27.4, 2.3], [18.2, 31.2, 2.1]);
  p.chain([23.2, 19.2, 2.8], [28.8, 25.4, 2.3], [26.4, 20, 2.1]);
}

/** Where the two riflemen of the squad stand (origin, scale). */
const SQUAD: [number, number, number][] = [
  [22, 9.4, 0.98],
  [57, 5.2, 1.08],
];

/** Rifle squad: two riflemen advancing, the second a step behind and further off. */
function infantry(): UnitArt {
  const [back, front] = SQUAD;
  const a = new Art();
  // the second man in the lighter "distance" tone, the leader in ink, cut free from him
  a.add('mid', (p) => rifleman(p.at(...back)));
  a.add('face', (p) => rifleman(p.at(...front)), { stroke: 3.2 });
  a.add('ink', (p) => rifleman(p.at(...front)));
  // the leader's pack catches the lamp; hairlines on helmet, pack and rifle
  a.add('mid', (p) => p.at(...front).rrect(10.8, 18.2, 5, 9.6, 1.6));
  a.add(
    'hi',
    (p) => {
      const q = p.at(...front);
      q.arc(22, 8.6, 5.6, 200, 268);
      q.line(18.2, 31.4, 33.6, 10);
      q.line(10.2, 27.2, 10.2, 18.6, 11.8, 17);
    },
    { stroke: 0.9 },
  );
  // both men make the outline of the hollow (dummy) print
  const art = a.build();
  const both = new Pen();
  rifleman(both.at(...back));
  rifleman(both.at(...front));
  return { layers: art.layers, outline: both.toString() };
}

/** Cavalry trooper at the trot: steel helmet, carbine slung across the back. */
function horseman(): UnitArt {
  const a = new Art();
  // far-side legs in the distance tone
  a.add('mid', (p) => {
    p.chain([70, 47, 3.8], [70.5, 57, 2.3], [69.4, 65.6, 1.7]);
    p.limb(69.2, 66, 1.9, 72, 68.6, 2);
    p.chain([45, 47, 4.4], [43.8, 55.6, 2.3], [48.4, 62.2, 1.6]);
    p.limb(48.6, 62.4, 1.7, 51.4, 64.4, 1.9);
  });
  a.add('ink', (p) => {
    // barrel of the horse: back, croup, belly, chest
    p.M(77, 34.6).C(68, 37.4, 56, 37.4, 46, 34.4).C(37.6, 32, 31.6, 35.2, 31.6, 41.2);
    p.C(31.6, 46.4, 35, 50, 40.2, 51.2).C(50, 53.4, 62, 52.8, 70.4, 51.6).C(76.6, 50.6, 81.4, 47, 82, 42);
    p.C(82.4, 38.4, 80.8, 35, 77, 34.6).Z();
    // neck, head, ears
    p.limb(76.5, 39, 7.2, 87.6, 23.6, 4.3);
    p.M(74, 34).C(77, 27, 81, 21.5, 85.6, 18.6).L(88.4, 21.2).C(84.4, 25, 81, 30, 79, 36).Z();
    p.limb(88, 21.2, 4.6, 99.4, 29.4, 2.7);
    p.circle(89.8, 25, 3.9);
    p.limb(86.4, 19.4, 1.3, 85.4, 13.6, 0.55);
    // tail
    p.M(33.2, 37.2).C(27, 38.6, 23.8, 45.6, 24.6, 56.4).C(26.8, 51.6, 29.6, 46.8, 33.4, 43.4).Z();
    // near fore (lifted at the knee), near hind (on the ground)
    p.chain([75.6, 46.2, 4.4], [83.8, 53.8, 2.5], [82.6, 62.6, 1.8]);
    p.limb(82.6, 62.8, 1.9, 85.4, 64.8, 2.1);
    p.chain([40, 46.6, 5.6], [33.8, 56.8, 2.5], [36.6, 65.8, 1.8]);
    p.limb(36.6, 66, 2, 39.4, 68.6, 2.1);
    // rider: helmet, head, torso, thigh, boot in the stirrup, arm to the reins
    helmet(p, 61.4, 8.2, 11.4);
    p.circle(62.6, 11, 3.4);
    p.limb(59.8, 16.8, 4.9, 57.6, 29.4, 4.5);
    p.chain([57.8, 30.4, 4.3], [66, 37.2, 3.1], [63.4, 46.2, 2.3]);
    p.limb(63.4, 46.4, 2.3, 67.4, 48, 1.5);
    p.chain([61, 18, 2.6], [64.8, 25.4, 2.1], [71.4, 27, 1.9]);
    // carbine across the back
    p.limb(49.4, 9, 0.95, 60.4, 28.6, 1.2);
  });
  // reins
  a.add('ink', (p) => p.line(71.4, 27, 84, 25.4, 97.6, 28.4), { stroke: 0.8 });
  a.add('mid', (p) => {
    // saddle cloth, horse's flank catching the lamp
    p.poly(50, 34.4, 64.6, 35.4, 63.4, 43.6, 51.2, 43);
    p.M(42, 38.6).C(50, 40.2, 62, 40.4, 72, 38.4).C(66, 42.6, 50, 43, 42, 38.6).Z();
  });
  a.add(
    'hi',
    (p) => {
      p.M(47, 35.4).C(56, 38, 68, 38, 76.6, 35.4);
      p.M(76, 33).C(78.8, 27, 82.4, 22.4, 86, 19.8);
      p.arc(61.4, 8.2, 5.2, 200, 268);
      p.M(33, 40).C(33.4, 37.4, 36, 35.6, 40, 35.6);
    },
    { stroke: 0.9 },
  );
  return a.build();
}

/** Officer in a peaked cap from the shoulders up, facing right (x = centre of the head). */
function officer(p: Pen, x: number, y: number, s: number): void {
  const q = p.at(x, y, s);
  q.limb(-1.6, 10.4, 5.4, -0.6, 5.8, 5.1);
  q.circle(0, 0, 3.9);
  // cap: crown, band, visor
  q.M(-4.9, -1.4).C(-5.2, -6.4, 5.4, -7.2, 5.6, -2.6).Z();
  q.rrect(-4.6, -3, 9.4, 2.6, 0.8);
  q.poly(3.8, -1.6, 8.4, -0.4, 3.8, -0.2);
}

/** Open staff car of the thirties: long bonnet, sweeping wings, two officers, command pennant. */
function staffCar(): UnitArt {
  const wheels: [number, number][] = [
    [31, 60],
    [92, 60],
  ];
  const a = new Art();
  a.add('ink', (p) => {
    // spare wheel on the tail
    p.circle(10.6, 46.4, 6.6);
    // body: rounded tail, doors, long bonnet, radiator
    p.M(12, 52.5).L(12, 45).C(12, 40, 15, 37.4, 20, 37.2).L(70, 37).L(101, 38);
    p.C(104.5, 38.1, 106.5, 40, 106.5, 43).L(106.5, 52.5).Z();
    // folded hood behind the rear seat
    p.M(14.4, 37.6).C(14.6, 34.4, 16.6, 33, 20, 33).L(29.4, 33.4).C(31.2, 33.6, 31.8, 35.4, 31.4, 37.6).Z();
    // officer in the back, driver in front
    officer(p, 39.6, 26.2, 1.05);
    officer(p, 59.4, 27.2, 0.98);
    // windscreen frame, leaning back
    p.poly(68.4, 37.6, 72.6, 22.6, 74.9, 22.6, 70.9, 37.6);
    // wings sweeping down to the running board
    p.M(17, 54).C(18.6, 46.4, 24, 44.4, 31, 44.4).C(38, 44.4, 43, 47, 45.6, 52.4).L(45.6, 54).Z();
    p.M(64, 55).C(71, 54, 75.4, 50.6, 79.6, 46.8).C(83.6, 43.8, 87.8, 42.8, 92.4, 42.8);
    p.C(99.6, 42.8, 104.6, 46.8, 107.8, 53.4).L(108.2, 55).Z();
    p.poly(44, 52.2, 70, 52.2, 70, 55, 44, 55);
    for (const [x, y] of wheels) p.circle(x, y, 9.4);
    // headlamp on its stem
    p.circle(100.4, 35.6, 3);
    p.poly(99.6, 37, 101.2, 37, 101.2, 43, 99.6, 43);
    // pennant staff on the wing
    p.limb(104.2, 45, 0.8, 104.2, 11.6, 0.6);
  });
  a.add('mid', (p) => {
    p.M(20, 39.8)
      .L(70, 39.6)
      .L(101, 40.4)
      .C(103.4, 40.5, 104.4, 41.8, 104.4, 43.6)
      .L(104.4, 50.4)
      .L(14.2, 50.4);
    p.L(14.2, 45.4).C(14.2, 42, 16, 40, 20, 39.8).Z();
    for (const [x, y] of wheels) p.circle(x, y, 5.6);
    p.circle(10.6, 46.4, 3.6);
  });
  a.add('ink', (p) => {
    for (const [x, y] of wheels) p.circle(x, y, 2);
    // door seams, bonnet louvres
    for (const x of [33.6, 50.8, 68.2]) p.poly(x, 40, x + 0.9, 40, x + 0.9, 50.4, x, 50.4);
    for (const x of [77, 80.5, 84, 87.5, 91]) p.poly(x, 42, x + 0.9, 42, x + 0.9, 48, x, 48);
  });
  // swallow-tailed command pennant flying back from the staff: pale cloth, ink hem
  const pennant = (p: Pen) =>
    p
      .M(104, 11.8)
      .C(97.6, 13, 91.4, 14, 84.4, 15.2)
      .L(89.4, 19.2)
      .L(84.4, 23.2)
      .C(91.4, 24, 97.6, 25, 104, 26.4);
  a.add('flag', (p) => pennant(p).Z());
  a.add('ink', (p) => pennant(p), { stroke: 1.2 });
  a.add(
    'hi',
    (p) => {
      p.line(20, 38, 70, 37.8, 101, 38.8);
      p.M(76, 47.4).C(80.4, 44.4, 86, 43.6, 92.4, 43.6);
      p.arc(31, 52.6, 10.4, 200, 290);
      for (const [x, y] of wheels) rim(p, x, y, 8.1);
      p.M(16, 36).C(16.6, 34.6, 18, 33.8, 20.4, 33.8);
    },
    { stroke: 0.9 },
  );
  return a.build();
}

const ART: Record<Exclude<UnitType, 'decoy'>, UnitArt> = {
  infantry: infantry(),
  motorized: truck(),
  armor: tank(),
  artillery: fieldGun(),
  recon: horseman(),
  hq: staffCar(),
};

/**
 * The art for a unit type. A decoy shows the formation it pretends to be; a decoy without a
 * disguise is a plywood tank.
 */
export function unitArt(type: Exclude<UnitType, 'decoy'> | 'decoy'): UnitArt {
  return type === 'decoy' ? ART.armor : ART[type];
}

// ---------------------------------------------------------------------------------------------
// Counter body (layout units of the 60 × 52 counter box)
// ---------------------------------------------------------------------------------------------

/** Frame of the counter face in the counter box, and its corner radius (die-cut). */
export const FACE = { x: 6, y: 12, w: 48, h: 32, r: 2.4 } as const;

/** Hairline bevel: light along the top and left edge, shade along the bottom and right. */
export const BEVEL_LIGHT = `M${FACE.x + 0.7} ${FACE.y + FACE.h - 2.6}L${FACE.x + 0.7} ${FACE.y + 2.6}Q${FACE.x + 0.7} ${FACE.y + 0.7} ${FACE.x + 2.6} ${FACE.y + 0.7}L${FACE.x + FACE.w - 2.6} ${FACE.y + 0.7}`;
export const BEVEL_SHADE = `M${FACE.x + FACE.w - 0.7} ${FACE.y + 2.6}L${FACE.x + FACE.w - 0.7} ${FACE.y + FACE.h - 2.6}Q${FACE.x + FACE.w - 0.7} ${FACE.y + FACE.h - 0.7} ${FACE.x + FACE.w - 2.6} ${FACE.y + FACE.h - 0.7}L${FACE.x + 2.6} ${FACE.y + FACE.h - 0.7}`;

/** Face outline (rounded rectangle) as path data. */
export const FACE_PATH = new Pen().rrect(FACE.x, FACE.y, FACE.w, FACE.h, FACE.r).toString();
/** Inner key line printed 2.2 units inside the die-cut edge. */
export const KEYLINE_PATH = new Pen()
  .rrect(FACE.x + 2.2, FACE.y + 2.2, FACE.w - 4.4, FACE.h - 4.4, 1.2)
  .toString();

/**
 * Paper grain of the cardboard: fixed specks (light and dark) and a few fibres, as path data so
 * the map and the HUD print the same grain.
 */
function grain(): { light: string; dark: string; fibre: string } {
  let s = 20260927;
  const rnd = () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
  const light = new Pen();
  const dark = new Pen();
  const fibre = new Pen();
  for (let i = 0; i < 150; i++) {
    const x = FACE.x + 1 + rnd() * (FACE.w - 2);
    const y = FACE.y + 1 + rnd() * (FACE.h - 2);
    const r = 0.14 + rnd() * 0.22;
    (i % 2 ? light : dark).circle(x, y, r);
  }
  for (let i = 0; i < 9; i++) {
    const x = FACE.x + 3 + rnd() * (FACE.w - 8);
    const y = FACE.y + 3 + rnd() * (FACE.h - 6);
    const a = rnd() * Math.PI;
    const l = 1.6 + rnd() * 2.4;
    fibre
      .M(x, y)
      .Q(
        x + Math.cos(a) * l * 0.5 + 0.4,
        y + Math.sin(a) * l * 0.5 - 0.4,
        x + Math.cos(a) * l,
        y + Math.sin(a) * l,
      );
  }
  return { light: light.toString(), dark: dark.toString(), fibre: fibre.toString() };
}
export const GRAIN = grain();

export type CounterLook = 'solid' | 'ghost' | 'decoy' | 'exposed';

/** The colours of one illustrated counter (css strings via the caller's `cssColor`). */
export interface IllustratedInks {
  faceTop: number;
  faceBottom: number;
  /** Die-cut edge (the cardboard's thickness) and the frame line. */
  edge: number;
  keyline: number;
  ink: number;
  mid: number;
  hi: number;
  flag: number;
  /** Silhouette printed hollow (dummy formations). */
  hollow: boolean;
  /** Dashed frame (remembered contacts, own decoys, unmasked decoys). */
  dashed: boolean;
  /** Cardboard body with thickness and drop shadow (not for ghosts: they are memories). */
  body: boolean;
}

/**
 * Colours of an illustrated counter in a faction colour. The ink is the faction colour taken
 * almost to black, so each nation's counters print like a two-colour job in its own ink.
 */
export function illustratedInks(color: number, look: CounterLook, unknown: boolean): IllustratedInks {
  const ghost = look === 'ghost';
  const faceTop = lighten(color, unknown ? 0.58 : ghost ? 0.5 : 0.34);
  const faceBottom = mixColor(faceTop, darken(color, 0.25), unknown || ghost ? 0.18 : 0.28);
  const ink = darken(color, ghost ? 0.66 : 0.84);
  return {
    faceTop,
    faceBottom,
    edge: darken(color, 0.52),
    keyline: darken(color, 0.4),
    ink,
    mid: mixColor(ink, faceBottom, ghost ? 0.4 : 0.3),
    hi: lighten(color, 0.8),
    flag: lighten(color, 0.9),
    hollow: look === 'decoy',
    dashed: look !== 'solid',
    body: !ghost,
  };
}
